import { useMemo, useState } from "react";
import { Bot, GitBranch, Plus, Route } from "lucide-react";
import { toast } from "react-toastify";
import ReactFlow, { Background, Controls, MiniMap } from "reactflow";

import "reactflow/dist/style.css";

import { getErrorMessage } from "../../Workspace/services/workspaceService";
import AddNodeDialog from "../dialogs/AddNodeDialog";
import workflowService from "../services/workflowService";
import { calculateGraphLayout } from "../utils/graphLayout";
import EmptyState from "./EmptyState";
import WorkflowNode from "./WorkflowNode";

const nodeTypes = {
    workflowNode: ({ data }) => (
        <WorkflowNode
            node={data.node}
            selected={data.selected}
            onClick={data.onClick}
            onChanged={data.onChanged}
            allNodes={data.allNodes}
            allTransitions={data.allTransitions}
        />
    ),
};

export default function WorkflowCanvas({ workflow }) {
    const {
        loading,
        workflow: workflowData,
        selectedStage,
        selectedNode,
        selectNode,
        refreshWorkflow,
    } = workflow;
    const [addNodeOpen, setAddNodeOpen] = useState(false);
    const nodes = useMemo(() => selectedStage?.nodes ?? [], [selectedStage]);
    const transitions = useMemo(() => selectedStage?.transitions ?? [], [selectedStage]);
    const allNodes = useMemo(
        () => (workflowData?.stages || []).flatMap((stage) =>
            (stage.nodes || []).map((node) => ({ ...node, stage_name: stage.name }))
        ),
        [workflowData]
    );
    const allTransitions = useMemo(
        () => (workflowData?.stages || []).flatMap((stage) => stage.transitions || []),
        [workflowData]
    );
    const graph = useMemo(() => {
        const layout = calculateGraphLayout(nodes, transitions);
        return {
            ...layout,
            nodes: layout.nodes.map((flowNode) => ({
                ...flowNode,
                data: {
                    ...flowNode.data,
                    selected: String(selectedNode?.id) === String(flowNode.data.node.id),
                    onClick: () => selectNode(flowNode.data.node),
                    onChanged: refreshWorkflow,
                    allNodes,
                    allTransitions,
                },
            })),
            edges: layout.edges.map((edge) => ({
                ...edge,
                labelStyle: { fill: "#536b91", fontSize: 11, fontWeight: 700 },
                labelBgStyle: { fill: "#ffffff", fillOpacity: 0.94 },
                labelBgPadding: [7, 4],
                labelBgBorderRadius: 7,
                style: { stroke: "#8dbfd8", strokeWidth: 2 },
            })),
        };
    }, [allNodes, allTransitions, nodes, refreshWorkflow, selectNode, selectedNode?.id, transitions]);

    const handleCreateRootNode = async (data) => {
        try {
            await workflowService.createNode({
                stage_id: selectedStage.id,
                name: data.name,
                description: data.description || null,
                node_type: data.node_type,
                can_skip: data.can_skip,
                metadata: data.metadata || {},
            });
            setAddNodeOpen(false);
            toast.success("Action added");
        } catch (error) {
            toast.error(getErrorMessage(error, "Unable to add the action."));
        }
    };

    if (loading && !workflowData) {
        return (
            <div className="flex h-full flex-col">
                <div className="h-28 animate-pulse border-b border-[#e1eaf3] bg-white" />
                <div className="flex flex-1 items-center justify-center">
                    <div className="space-y-5">
                        {[0, 1, 2].map((item) => <div key={item} className="h-24 w-72 animate-pulse rounded-2xl border border-[#e1eaf3] bg-white" />)}
                    </div>
                </div>
            </div>
        );
    }

    if (!selectedStage) return <EmptyState />;

    return (
        <div className="flex h-full min-h-0 flex-col">
            <header className="flex min-h-[92px] items-center justify-between gap-5 border-b border-[#dce8f3] bg-white px-5 py-4 lg:px-6">
                <div className="flex min-w-0 items-center gap-3.5">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#e7f5fc] text-[#147db5]"><GitBranch className="h-5 w-5" /></span>
                    <div className="min-w-0">
                        <div className="flex items-center gap-2">
                            <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#1689c1]">Stage {selectedStage.display_order}</span>
                            {!selectedStage.is_active && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[9px] font-bold text-slate-600">Inactive</span>}
                            {selectedStage.is_closed && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[9px] font-bold text-emerald-700">Closed stage</span>}
                        </div>
                        <h2 className="mt-0.5 truncate text-xl font-bold tracking-[-0.025em] text-[#102b5c]">{selectedStage.name}</h2>
                        <p className="mt-1 flex items-center gap-2 text-xs font-medium text-[#7185a3]"><span>{nodes.length} actions</span><span className="h-1 w-1 rounded-full bg-[#afbdcc]" /><span>{transitions.length} connections</span><span className="h-1 w-1 rounded-full bg-[#afbdcc]" /><span>{selectedStage.probability ?? 0}% probability</span></p>
                    </div>
                </div>
                <button type="button" onClick={() => setAddNodeOpen(true)} className="inline-flex h-10 shrink-0 items-center gap-2 rounded-xl bg-[#147db5] px-4 text-sm font-bold text-white shadow-md shadow-[#147db5]/15 transition hover:bg-[#0f6f9f]"><Plus className="h-4 w-4" /> Add action</button>
            </header>

            <div className="relative min-h-0 flex-1">
                {nodes.length === 0 ? (
                    <div className="flex h-full items-center justify-center p-8">
                        <div className="max-w-sm text-center">
                            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#eaf6fc] text-[#147db5]"><Bot className="h-6 w-6" /></span>
                            <h3 className="mt-4 text-lg font-bold text-[#173552]">Add the first action</h3>
                            <p className="mt-2 text-sm leading-6 text-[#7185a3]">Create a manual task, decision, or automation to begin this stage.</p>
                            <button type="button" onClick={() => setAddNodeOpen(true)} className="mt-5 inline-flex h-10 items-center gap-2 rounded-xl bg-[#147db5] px-4 text-sm font-bold text-white"><Plus className="h-4 w-4" /> Add first action</button>
                        </div>
                    </div>
                ) : (
                    <ReactFlow
                        key={selectedStage.id}
                        onInit={(instance) => {
                            const firstNode = [...graph.nodes].sort(
                                (first, second) => first.position.y - second.position.y
                            )[0];
                            if (firstNode) {
                                instance.setCenter(
                                    firstNode.position.x + 140,
                                    firstNode.position.y + 64,
                                    { zoom: 0.82, duration: 0 }
                                );
                            }
                        }}
                        minZoom={0.45}
                        maxZoom={1.35}
                        nodes={graph.nodes}
                        edges={graph.edges}
                        nodeTypes={nodeTypes}
                        nodesDraggable={false}
                        nodesConnectable={false}
                        elementsSelectable
                        zoomOnDoubleClick={false}
                        onPaneClick={() => selectNode(null)}
                        proOptions={{ hideAttribution: true }}
                    >
                        <Background color="#cbddeb" gap={22} size={1} />
                        <MiniMap pannable zoomable nodeColor="#d8edf8" maskColor="rgba(240, 247, 252, 0.75)" />
                        <Controls showInteractive={false} />
                    </ReactFlow>
                )}
                <div className="pointer-events-none absolute bottom-4 left-4 inline-flex items-center gap-2 rounded-xl border border-[#dce8f3] bg-white/90 px-3 py-2 text-[11px] font-semibold text-[#7185a3] shadow-sm backdrop-blur">
                    <Route className="h-3.5 w-3.5 text-[#147db5]" /> Select an action to edit it
                </div>
            </div>

            <AddNodeDialog
                open={addNodeOpen}
                stageId={selectedStage.id}
                onClose={() => setAddNodeOpen(false)}
                onSave={handleCreateRootNode}
            />
        </div>
    );
}
